import type { SchemaSite } from '@/utils/schema-site';
import { getSchemaSite } from '@/lib/schema-site.server';
import { use, useEffect, useRef, Suspense, Fragment } from 'react';
import { Await, type LoaderFunctionArgs, type ShouldRevalidateFunctionArgs, redirect } from 'react-router';
import type { Route } from './+types/_app.p.$';
import { getTranslation } from '@salesforce/storefront-next-runtime/i18n';
import { DEFAULT_SITE_NAME_KEY } from '@/components/seo-meta';
import { NormalizedApiError } from '@/lib/api/normalized-api-error';
import { createApiClients } from '@/lib/api-clients.server';
import { siteContext } from '@salesforce/storefront-next-runtime/site-context';
import ProductContentSkeleton from '@/components/product-skeleton';
import { ProductView, EventCourseView } from '@/components/product-view-custom';
import PdpStickyAnchorBar, {
    PDP_RECOMMENDATIONS_ANCHOR_ID,
    pdpAnchorScrollMarginClass,
} from '@/components/product-view-custom/pdp-sticky-anchor-bar';
import ChildProducts from '@/components/product-view/child-products';
import { useAnalytics } from '@/hooks/use-analytics';
import { Region } from '@/components/region';
import { attachPageComponentData, fetchPageOrNull, type PageWithComponentData } from '@/lib/util/pageLoader.server';
import { notFoundResponse } from '@/lib/not-found.server';
import { JsonLd } from '@/components/json-ld';
import { getLogger } from '@/lib/logger.server';
import { fetchCategory } from '@/lib/api/categories.server';
import { isLeafCategory } from '@/lib/category-link-target';
import SeoLinkingWrapper from '@/components/seo-linking/seo-linking-wrapper';
import type { SCAPIShopperProductsSchemas, SCAPIShopperStoresSchemas } from '@/types';
import { buildUrlFromContext } from '@/lib/url.server';
import { PageType } from '@/lib/decorators/page-type';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { UITarget } from '@/targets/ui-target';
import { EATALY_BREADCRUMB_DEFAULT_BACKGROUND_CLASS } from '@/components/eataly-breadcrumbs';
import type { EatalyBreadcrumbLoaderResult } from '@/components/eataly-breadcrumbs/types';
import { buildEatalyProductBreadcrumbItems } from '@/lib/breadcrumb/build-eataly-breadcrumb-items';
import { resolveBreadcrumbMergeHomeWithFirstItem } from '@/lib/breadcrumb/resolve-breadcrumb-merge-home';
import type { BrandContent } from '@/lib/api/fetch-brand';
import type { SupplyChainContent } from '@/lib/api/fetch-supply-chain';
import { fetchShopperCustomObject } from '@/lib/api/custom-objects';
import { getErrorMessage } from '@/lib/utils';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { getPublicOrigin } from '@/utils/schema-url';
import { generateProductSchema } from 'src/utils/product-schema';
import { generateBreadcrumbSchema } from '@/utils/breadcrumb-schema';

import {
    isProductSet,
    isEventOrCourseProduct,
    createProductUrl,
    isPhysicalProduct,
    getPrimaryProductImageUrl,
} from '@/lib/product-utils';
import { PDP_IMG_TYPES } from '@/lib/product-image-view-types';
import { getMultipackContentProduct } from '@/lib/multipack';
import { isWineProduct, WINE_VINTAGE_YEAR_ATTRIBUTE_ID } from '@/lib/wine-vintage';
import { GIFT_CARD_SIZE_ATTRIBUTE_ID, isGiftCardProduct } from '@/lib/gift-card-product-utils';
import { findVariantByAttributeValue, getDefaultVariantForAttribute } from '@/lib/variation-attribute-variant';
import {
    EVENT_INSTANCE_ATTRIBUTE_ID,
    getDefaultEventOccurrence,
    isEventSeriesProduct,
    sortEventOccurrences,
} from '@/lib/event-variation';
import { ProductProvider } from '@/providers/product-context';
import { shouldSkipRevalidationForCartOrWishlistAndNewsletterActions } from '@/lib/should-revalidate';
import { normalizeCategoryPath } from '@/lib/category-url';
import { isValidPrimaryShoppingCategoryPath } from '@/lib/primary-shopping-category';
import { readPrimaryShoppingCategoryPath } from '@/lib/primary-shopping-category.server';
import { findNavigationPillar } from '@/lib/navigation-pillars';
import { getNavigationPillarRootIds } from '@/lib/navigation-pillars.server';
import type { RouteDataWithShoppingContext } from '@/lib/shopping-context';
import { isCartAccessoryProductItem } from '@/lib/cart-accessory-utils';
import { fetchHelpCenterFaqCategory } from '@/lib/help-center-faq-placements.server';
import type { HelpCenterCategory } from '@/lib/api/help-center.server';
import { getSitePreferences } from '@/lib/preferences.server';
import { resolvePdpContentGroup } from '@/lib/analytics/content-group';
import { attachAnalytics, type RouteDataWithAnalytics } from '@/lib/analytics/route-analytics';
import { buildPageSeoDescriptors, resolvePageSeo } from '@/lib/page-seo';

export type ProductPageData = RouteDataWithShoppingContext &
    RouteDataWithAnalytics & {
        product: Promise<SCAPIShopperProductsSchemas['Product']>;
        /** Sellable sessions for an Eventbrite series, loaded eagerly for the above-the-fold date selector. */
        eventOccurrences: SCAPIShopperProductsSchemas['Product'][];
        /** Resolves with the full PDP trail after the same `product` + `category` fetches as the page (deferred, non-blocking loader). */
        breadcrumbItems: Promise<EatalyBreadcrumbLoaderResult>;
        /** Breadcrumb strip background (Eataly default layout). */
        breadcrumbBackgroundClass: string;
        /** Pillar entry path for the home icon (cookie or product pillar). */
        breadcrumbHomeTo?: string;
        category: Promise<SCAPIShopperProductsSchemas['Category'] | undefined>;
        page: PageWithComponentData | Promise<PageWithComponentData | null> | null;
        /** Parent category (levels 1) for SEO sibling links when the primary category is a leaf. */
        parentCategoryForSeo: Promise<SCAPIShopperProductsSchemas['Category'] | undefined>;
        seoLinkingData: Promise<{
            category: SCAPIShopperProductsSchemas['Category'] | undefined;
            parentCategoryForSeo: SCAPIShopperProductsSchemas['Category'] | undefined;
        }>;
        pageKey: string;
        pageUrl: string;
        seoTitle?: string;
        seoDescription?: string;
        seoRawTitle?: boolean;
        seoSiteName?: string;
        seoImage?: string;
        schemaSite?: SchemaSite;
        productSchema: Promise<ReturnType<typeof generateProductSchema> | null>;
        brandCustomObject: Promise<BrandContent | undefined>;
        supplyChainCustomObject: Promise<SupplyChainContent | undefined>;
        /** Store associated with an event/course product (via `c_eventStoreId`), for the "Location e contatti" tab. Deferred, non-critical. */
        eventStore: Promise<SCAPIShopperStoresSchemas['Store'] | undefined>;
        primaryCategoryPath: string | null;
        /** Gift card PDP FAQ category; null promise for non-gift-card products. */
        faqCategoryPromise: Promise<HelpCenterCategory | null>;
    };

function getProductIdFromSlugAndProductId(slugAndProductId: string): string {
    const leaf = slugAndProductId.split('/').filter(Boolean).at(-1) ?? '';
    const withoutExtension = leaf.endsWith('.html') ? leaf.slice(0, -5) : leaf;
    const parts = withoutExtension.split('-').filter(Boolean);
    return parts.at(-1) ?? '';
}

async function fetchProductData(
    clients: ReturnType<typeof createApiClients>,
    productId: string,
    currency: string
): Promise<SCAPIShopperProductsSchemas['Product']> {
    return clients.shopperProducts
        .getProduct({
            params: {
                path: {
                    id: productId,
                },
                query: {
                    expand: [
                        'availability',
                        'bundled_products',
                        'images',
                        'options',
                        'page_meta_tags',
                        'prices',
                        'promotions',
                        'set_products',
                        'variations',
                    ],
                    allImages: true,
                    imgTypes: PDP_IMG_TYPES,
                    perPricebook: true,
                    ...(currency ? { currency } : {}),
                },
            },
        })
        .then(({ data }) => data);
}

async function fetchEventOccurrences(
    clients: ReturnType<typeof createApiClients>,
    product: SCAPIShopperProductsSchemas['Product'],
    currency: string
): Promise<SCAPIShopperProductsSchemas['Product'][]> {
    const ids = Array.from(
        new Set((product.variants ?? []).map((variant) => variant.productId).filter((id): id is string => Boolean(id)))
    );
    if (ids.length === 0) {
        return [];
    }

    const { data } = await clients.shopperProducts.getProducts({
        params: {
            query: {
                ids,
                expand: ['availability', 'prices'],
                perPricebook: true,
                ...(currency ? { currency } : {}),
            },
        },
    });

    return sortEventOccurrences((data?.data ?? []) as SCAPIShopperProductsSchemas['Product'][]);
}

function isProductNotFound(error: unknown): boolean {
    if (error instanceof NormalizedApiError && error.status === 404) {
        return true;
    }

    return error instanceof Error && error.message.toLowerCase().includes('not found');
}

async function fetchProductOr404(
    args: LoaderFunctionArgs,
    clients: ReturnType<typeof createApiClients>,
    productId: string,
    currency: string
): Promise<SCAPIShopperProductsSchemas['Product']> {
    let productData: SCAPIShopperProductsSchemas['Product'];
    try {
        productData = await fetchProductData(clients, productId, currency);
    } catch (error) {
        if (isProductNotFound(error)) {
            throw await notFoundResponse(args);
        }
        throw error;
    }

    if (!productData || isCartAccessoryProductItem(productData) || isProductSet(productData)) {
        throw await notFoundResponse(args);
    }

    return productData;
}

async function resolveRouteProduct(
    args: LoaderFunctionArgs,
    clients: ReturnType<typeof createApiClients>,
    currency: string
): Promise<{ routeProductId: string; routeProductData: SCAPIShopperProductsSchemas['Product'] }> {
    const { params } = args;
    const slugAndProductId = params.productId || params.slugAndProductId || params['*'] || '';
    const routeProductId = getProductIdFromSlugAndProductId(slugAndProductId);
    if (!routeProductId) {
        throw await notFoundResponse(args);
    }

    return {
        routeProductId,
        routeProductData: await fetchProductOr404(args, clients, routeProductId, currency),
    };
}
@PageType({
    name: 'Product Detail Page',
    description: 'Product detail page with product information, images, and recommendations',
    supportedAspectTypes: ['pdp'],
})
@RegionDefinition([
    {
        id: 'engagementContent',
        name: 'Engagement Content Region',
        description: 'Engagement content region for recommendations and related products below main content',
        maxComponents: 4,
        componentTypeInclusions: [
            'carousels.crossSellCarousel',
            'content.recommendedRecipe',
            'content.recommendedCourse',
            'content.editorialTextMedia',
        ],
    },
    {
        id: 'productTabs',
        name: 'Product Tabs Region',
        description: 'Gift card PDP tabs with editorial accordion and optional how-it-works content',
        maxComponents: 1,
        componentTypeInclusions: ['content.giftCardProductTabs'],
    },
])
export class ProductPageMetadata {}

async function fetchProductBrandCustomObject(
    context: LoaderFunctionArgs['context'],
    productPromise: Promise<SCAPIShopperProductsSchemas['Product']>
): Promise<BrandContent | undefined> {
    const logger = getLogger(context);
    return productPromise
        .then((product) => {
            const contentProduct = getMultipackContentProduct(product);
            const brandID = contentProduct.c_brandID ?? product.c_brandID;
            if (!brandID) {
                return undefined;
            }

            return fetchShopperCustomObject<BrandContent>(context, 'Brand', brandID).catch((e) => {
                logger.error('Error fetching brand custom object', { brandID, error: getErrorMessage(e) });
                return undefined;
            });
        })
        .catch(() => undefined);
}

async function fetchProductSupplyChainCustomObject(
    context: LoaderFunctionArgs['context'],
    productPromise: Promise<SCAPIShopperProductsSchemas['Product']>
): Promise<SupplyChainContent | undefined> {
    const logger = getLogger(context);
    return productPromise
        .then((product) => {
            const contentProduct = getMultipackContentProduct(product);
            const supplyChainID = contentProduct.c_supplyChainID ?? product.c_supplyChainID;

            if (!supplyChainID) {
                return undefined;
            }

            return fetchShopperCustomObject<SupplyChainContent>(context, 'SupplyChain', supplyChainID).catch((e) => {
                logger.error('Error fetching supply chain custom object', {
                    supplyChainID,
                    error: getErrorMessage(e),
                });
                return undefined;
            });
        })
        .catch(() => undefined);
}

/**
 * Fetches the store associated with an event/course product (`c_eventStoreId`) via the
 * Shopper Stores API. Non-critical below-the-fold tab data, so this resolves as a deferred
 * Promise and never blocks the loader; any failure (missing id, non-event product, API error)
 * resolves to `undefined` and the "Location e contatti" tab simply renders nothing.
 */
async function fetchEventStore(
    clients: ReturnType<typeof createApiClients>,
    context: LoaderFunctionArgs['context'],
    productPromise: Promise<SCAPIShopperProductsSchemas['Product']>
): Promise<SCAPIShopperStoresSchemas['Store'] | undefined> {
    const logger = getLogger(context);
    const siteCtx = context.get(siteContext);
    const siteId = siteCtx?.site?.id;

    return productPromise
        .then((product) => {
            const storeId = product.c_eventStoreId;
            if (!storeId || !siteId || !isEventOrCourseProduct(product)) {
                return undefined;
            }

            return clients.shopperStores
                .getStores({
                    params: {
                        query: {
                            ids: storeId,
                            siteId,
                        },
                    },
                })
                .then(({ data }) => data?.data?.[0])
                .catch((e) => {
                    logger.error('Error fetching event store', { storeId, error: getErrorMessage(e) });
                    return undefined;
                });
        })
        .catch(() => undefined);
}

/**
 * Server-side loader function that fetches product data and category information.
 * This function runs on the server during SSR and can access cookies for store information.
 * @returns Object containing product, category, page data, and component data promises
 */
export async function loader(args: LoaderFunctionArgs): Promise<ProductPageData | Response> {
    const { request, context } = args;
    const logger = getLogger(context);
    const requestUrl = new URL(request.url);
    const { searchParams } = requestUrl;
    const variantPid = searchParams.get('pid');
    const wineVintageYear = searchParams.get(WINE_VINTAGE_YEAR_ATTRIBUTE_ID);
    const giftCardSize = searchParams.get(GIFT_CARD_SIZE_ATTRIBUTE_ID);
    const eventInstanceId = searchParams.get(EVENT_INSTANCE_ATTRIBUTE_ID);

    // Get currency from context for product pricing
    const siteCtx = context.get(siteContext);
    if (!siteCtx) {
        logger.error('Product: site context is not available');
        throw new Response('Site context is not available', { status: 500 });
    }

    const currency = siteCtx.currency;

    const clients = createApiClients(context);
    const { routeProductId, routeProductData } = await resolveRouteProduct(args, clients, currency);

    const canonicalBarePath = createProductUrl(routeProductData.c_url || '#');
    const canonicalPathname = canonicalBarePath === '#' ? null : buildUrlFromContext(canonicalBarePath, context);

    if (canonicalPathname && requestUrl.pathname !== canonicalPathname) {
        return redirect(`${canonicalPathname}${requestUrl.search}`, 301);
    }

    // Help Center FAQs only for gift card PDPs — avoid prefs/Help Center work on other products.
    let faqCategoryPromise: Promise<HelpCenterCategory | null> = Promise.resolve(null);
    if (isGiftCardProduct(routeProductData)) {
        const sitePreferences = await getSitePreferences(context);
        faqCategoryPromise = fetchHelpCenterFaqCategory(context, sitePreferences.zendeskFaq, 'giftCardPdp');
    }

    // Wine / gift card: attribute query selects the SKU (no pid). Missing attr → default cut/year.
    // Event series / wine / gift card: attribute query selects the SKU (no pid).
    // Other products: optional ?pid= still loads that variant.
    let productData = routeProductData;
    let eventOccurrences: SCAPIShopperProductsSchemas['Product'][] = [];
    if (isEventSeriesProduct(routeProductData)) {
        const eventVariantId = eventInstanceId
            ? findVariantByAttributeValue(routeProductData, EVENT_INSTANCE_ATTRIBUTE_ID, eventInstanceId)?.productId
            : undefined;
        const occurrencesPromise = fetchEventOccurrences(clients, routeProductData, currency);

        if (eventVariantId) {
            [eventOccurrences, productData] = await Promise.all([
                occurrencesPromise,
                fetchProductOr404(args, clients, eventVariantId, currency),
            ]);
            eventOccurrences = eventOccurrences.map((occurrence) =>
                occurrence.id === productData.id ? productData : occurrence
            );
        } else {
            eventOccurrences = await occurrencesPromise;
            const defaultOccurrence = getDefaultEventOccurrence(eventOccurrences);
            if (defaultOccurrence?.id && canonicalPathname) {
                const params = new URLSearchParams(requestUrl.search);
                params.set(EVENT_INSTANCE_ATTRIBUTE_ID, defaultOccurrence.id);
                params.delete('pid');
                return redirect(`${canonicalPathname}?${params.toString()}`, 302);
            }
            if (defaultOccurrence?.id) {
                productData = await fetchProductOr404(args, clients, defaultOccurrence.id, currency);
            }
        }
    } else {
        const attributeSkuSelection = isWineProduct(routeProductData)
            ? { attributeId: WINE_VINTAGE_YEAR_ATTRIBUTE_ID, selectedValue: wineVintageYear }
            : isGiftCardProduct(routeProductData)
              ? { attributeId: GIFT_CARD_SIZE_ATTRIBUTE_ID, selectedValue: giftCardSize }
              : null;

        if (attributeSkuSelection) {
            const { attributeId, selectedValue } = attributeSkuSelection;
            if (!selectedValue) {
                const defaultValue = getDefaultVariantForAttribute(routeProductData, attributeId)?.variationValues?.[
                    attributeId
                ];
                if (defaultValue && canonicalPathname) {
                    const params = new URLSearchParams(requestUrl.search);
                    params.set(attributeId, defaultValue);
                    return redirect(`${canonicalPathname}?${params.toString()}`, 302);
                }
            } else {
                const attributeVariantId = findVariantByAttributeValue(
                    routeProductData,
                    attributeId,
                    selectedValue
                )?.productId;
                if (attributeVariantId && attributeVariantId !== routeProductId) {
                    productData = await fetchProductOr404(args, clients, attributeVariantId, currency);
                }
            }
        } else if (variantPid && variantPid !== routeProductId) {
            productData = await fetchProductOr404(args, clients, variantPid, currency);
        }
    }
    const productPromise = Promise.resolve(productData);

    const primaryCategoryIdPromise = productPromise.then((product) => {
        if (!product) {
            return undefined;
        }

        if (product.primaryCategoryId) {
            return product.primaryCategoryId;
        }

        if (!product.primaryCategoryId && product.master?.masterId) {
            return clients.shopperProducts
                .getProduct({
                    params: {
                        path: {
                            id: product.master.masterId,
                        },
                    },
                })
                .then(({ data: masterProduct }) => masterProduct.primaryCategoryId || undefined)
                .catch(() => undefined);
        }

        return undefined;
    });

    const categoryPromise = primaryCategoryIdPromise.then((primaryCategoryId) => {
        if (!primaryCategoryId) {
            return undefined;
        }

        return clients.shopperProducts
            .getCategory({
                params: {
                    path: {
                        id: primaryCategoryId,
                    },
                    query: {
                        levels: 1,
                    },
                },
            })
            .then(({ data }) => data)
            .catch(() => undefined);
    });

    const parentCategoryForSeoPromise = categoryPromise
        .then((cat) => {
            if (!cat || !isLeafCategory(cat) || !cat.parentCategoryId) {
                return undefined;
            }
            return fetchCategory(context, cat.parentCategoryId, 1).catch(() => undefined);
        })
        .catch(() => undefined);

    const pageUrl = buildCanonicalUrl(getPublicOrigin(request), requestUrl.pathname, requestUrl.search);
    const productSchemaPromise = productPromise
        .then((product) => {
            try {
                return generateProductSchema(product, pageUrl, getSchemaSite(args));
            } catch (error) {
                logger.error('Error generating product schema in loader', { error });
                return null;
            }
        })
        .catch(() => null);
    // const pdpEditorialPromise = createPdpEditorialPromise(context, productPromise);

    const brandCustomObjectPromise = fetchProductBrandCustomObject(context, productPromise);
    const supplyChainCustomObjectPromise = fetchProductSupplyChainCustomObject(context, productPromise);
    const eventStorePromise = fetchEventStore(clients, context, productPromise);

    /**
     * Page Designer page assigned to the product, falling back to the one assigned to its primary category.
     * Both lookups run in parallel; component loaders start only for the page that is used.
     */
    const pagePromise: Promise<PageWithComponentData | null> = Promise.all([
        fetchPageOrNull(args, { aspectType: 'pdp', productId: routeProductId }),
        primaryCategoryIdPromise.then((primaryCategoryId) =>
            primaryCategoryId ? fetchPageOrNull(args, { aspectType: 'pdp', categoryId: primaryCategoryId }) : null
        ),
    ]).then(([productPage, categoryPage]) => {
        const page = productPage ?? categoryPage;
        return page ? attachPageComponentData(args, page, { criticalRegions: ['productTabs'] }) : null;
    });

    const normalizedPillarUrl = normalizeCategoryPath(productData.c_pillarUrl);
    const primaryCategoryPath =
        normalizedPillarUrl && isValidPrimaryShoppingCategoryPath(normalizedPillarUrl) ? normalizedPillarUrl : null;

    const navigationPillar = findNavigationPillar(
        await getNavigationPillarRootIds(context),
        productData.c_pillarCategoryId
    );

    const primaryCategoryPathFromCookie = await readPrimaryShoppingCategoryPath(request, context);
    const breadcrumbHomeTo = primaryCategoryPathFromCookie ?? primaryCategoryPath ?? undefined;

    // Deferred trail: no `await` here — the loader returns immediately with a Promise, like `productSchema`.
    // `.then` runs only after `productPromise` and `categoryPromise` resolve; the route is not blocked on that.
    const breadcrumbItems = Promise.all([productPromise, categoryPromise]).then(([product, category]) => {
        const items = buildEatalyProductBreadcrumbItems(product, category).map((item) => ({
            ...item,
            href: item.href && item.href !== '#' ? buildUrlFromContext(item.href, context) : item.href,
        }));
        return {
            items,
            mergeHomeWithFirstItem: resolveBreadcrumbMergeHomeWithFirstItem({
                items,
                homeTo: breadcrumbHomeTo,
                currentPathname: requestUrl.pathname,
            }),
        };
    });

    const seoLinkingDataPromise = Promise.all([categoryPromise, parentCategoryForSeoPromise]).then(
        ([category, parentCategoryForSeo]) => ({
            category,
            parentCategoryForSeo,
        })
    );

    const page = await pagePromise;

    // Same inputs the document `<title>` uses, so page_view matches the head tag before the
    // deferred product promise resolves.
    const {
        title: seoTitle,
        description: seoDescription,
        rawTitle: seoRawTitle,
    } = resolvePageSeo({
        title: productData.name,
        description: productData.shortDescription,
        pageTitle: productData.pageTitle,
        pageDescription: productData.pageDescription,
        pageMetaTags: productData.pageMetaTags,
    });
    const { t } = getTranslation(args.context);

    return attachAnalytics(
        {
            product: productPromise,
            eventOccurrences,
            category: categoryPromise,
            breadcrumbItems,
            breadcrumbBackgroundClass: isEventOrCourseProduct(productData)
                ? 'bg-neutral-white'
                : EATALY_BREADCRUMB_DEFAULT_BACKGROUND_CLASS,
            breadcrumbHomeTo,
            parentCategoryForSeo: parentCategoryForSeoPromise,
            seoLinkingData: seoLinkingDataPromise,
            page,
            pageKey: routeProductId,
            pageUrl,
            seoTitle,
            seoDescription,
            seoRawTitle,
            seoSiteName: t(DEFAULT_SITE_NAME_KEY, { ns: 'common' }),
            seoImage: getPrimaryProductImageUrl(productData),
            schemaSite: getSchemaSite(args),
            productSchema: productSchemaPromise,
            brandCustomObject: brandCustomObjectPromise,
            supplyChainCustomObject: supplyChainCustomObjectPromise,
            eventStore: eventStorePromise,
            primaryCategoryPath,
            faqCategoryPromise,
            shoppingContext: {
                primaryCategoryPath,
                navigationPillar,
                ...(isPhysicalProduct(productData) ? { recentlyViewedProductId: productData.id } : {}),
            },
        },
        resolvePdpContentGroup(productData),
        { title: seoTitle ?? '', rawTitle: seoRawTitle }
    );
}

export const meta: Route.MetaFunction = ({ loaderData, error, matches }) => {
    const inherited = matches.at(-2)?.meta ?? [];
    if (!loaderData || error || !loaderData.seoSiteName) return [...inherited];

    return [
        ...inherited,
        ...buildPageSeoDescriptors({
            title: loaderData.seoTitle,
            rawTitle: loaderData.seoRawTitle,
            description: loaderData.seoDescription,
            siteName: loaderData.seoSiteName,
            openGraph: {
                type: 'product',
                url: loaderData.pageUrl,
                image: loaderData.seoImage,
            },
        }),
    ];
};

/**
 * Prevent loader from re-running on variant parameter changes to avoid skeleton
 * https://reactrouter.com/start/data/route-object#shouldrevalidate
 * we don't want the page to show skeleton when loading variant product after first initial load
 */
export function shouldRevalidate({
    currentUrl,
    nextUrl,
    defaultShouldRevalidate,
    formAction,
}: ShouldRevalidateFunctionArgs) {
    if (shouldSkipRevalidationForCartOrWishlistAndNewsletterActions({ currentUrl, nextUrl, formAction })) {
        return false;
    }

    // Revalidate if pathname changes (different product)
    if (currentUrl.pathname !== nextUrl.pathname) {
        return true;
    }

    // Revalidate if pid parameter changes (different variant product)
    const currentPid = currentUrl.searchParams.get('pid');
    const nextPid = nextUrl.searchParams.get('pid');
    if (currentPid !== nextPid) {
        return true;
    }

    // Wine vintage: attribute query drives which variant SKU the loader fetches
    const currentWineYear = currentUrl.searchParams.get(WINE_VINTAGE_YEAR_ATTRIBUTE_ID);
    const nextWineYear = nextUrl.searchParams.get(WINE_VINTAGE_YEAR_ATTRIBUTE_ID);
    if (currentWineYear !== nextWineYear) {
        return true;
    }

    // Gift card cut: same as wine — loader fetches the selected denomination SKU
    const currentGiftCardSize = currentUrl.searchParams.get(GIFT_CARD_SIZE_ATTRIBUTE_ID);
    const nextGiftCardSize = nextUrl.searchParams.get(GIFT_CARD_SIZE_ATTRIBUTE_ID);
    if (currentGiftCardSize !== nextGiftCardSize) {
        return true;
    }

    // Event occurrence: attribute query resolves the selected sellable session SKU.
    const currentEventInstance = currentUrl.searchParams.get(EVENT_INSTANCE_ATTRIBUTE_ID);
    const nextEventInstance = nextUrl.searchParams.get(EVENT_INSTANCE_ATTRIBUTE_ID);
    if (currentEventInstance !== nextEventInstance) {
        return true;
    }

    // Soft revalidate only when the URL is unchanged (e.g. revalidator.revalidate() / store change).
    // React Router sets defaultShouldRevalidate=true whenever search changes — do not treat that as
    // a full reload, or color/size clicks re-fetch the Page Designer aspect page.
    if (currentUrl.search === nextUrl.search && defaultShouldRevalidate) {
        return true;
    }

    // Don't revalidate for other search parameter changes (color, size, etc.)
    return false;
}

function ProductContent({
    product,
    brandCustomObject,
    supplyChainCustomObject,
    eventStore,
    eventOccurrences,
    page,
    faqCategoryPromise,
}: {
    product: SCAPIShopperProductsSchemas['Product'];
    page: ProductPageData['page'];
    brandCustomObject: Promise<BrandContent | undefined>;
    supplyChainCustomObject: Promise<SupplyChainContent | undefined>;
    eventStore: ProductPageData['eventStore'];
    faqCategoryPromise: ProductPageData['faqCategoryPromise'];
    eventOccurrences: ProductPageData['eventOccurrences'];
}) {
    const analytics = useAnalytics();
    const lastTrackedProductIdRef = useRef<string | null>(null);

    // Track product view on mount and whenever product changes
    useEffect(() => {
        // Only track if we haven't already tracked this product
        if (product.id !== lastTrackedProductIdRef.current) {
            void analytics.trackViewProduct({
                product,
            });

            lastTrackedProductIdRef.current = product.id;
        }
    }, [analytics, product]);

    const isProductASet = isProductSet(product);
    const isEventOrCourse = isEventOrCourseProduct(product);
    const showPdpStickyAnchorBar = isPhysicalProduct(product);

    return (
        <ProductProvider product={product}>
            <div>
                {showPdpStickyAnchorBar ? (
                    <Suspense fallback={null}>
                        <Await resolve={page}>
                            {(resolvedPage) => {
                                const region = resolvedPage?.regions?.find((r) => r.id === 'engagementContent');
                                const hasRecommendations = (region?.components?.length ?? 0) > 0;
                                return <PdpStickyAnchorBar hasRecommendations={hasRecommendations} />;
                            }}
                        </Await>
                    </Suspense>
                ) : null}
                {isEventOrCourse ? (
                    <EventCourseView product={product} eventStore={eventStore} eventOccurrences={eventOccurrences} />
                ) : isProductASet ? (
                    <>
                        <ProductView
                            product={product}
                            brandCustomObject={brandCustomObject}
                            supplyChainCustomObject={supplyChainCustomObject}
                            page={page}
                            faqCategoryPromise={faqCategoryPromise}
                        />
                        <ChildProducts parentProduct={product} />
                    </>
                ) : (
                    <ProductView
                        product={product}
                        brandCustomObject={brandCustomObject}
                        supplyChainCustomObject={supplyChainCustomObject}
                        page={page}
                        faqCategoryPromise={faqCategoryPromise}
                    />
                )}
                <div
                    id={PDP_RECOMMENDATIONS_ANCHOR_ID}
                    className={pdpAnchorScrollMarginClass}
                    data-testid="pdp-recommendations">
                    <Region page={page} regionId="engagementContent" />
                </div>
                <UITarget targetId="sfcc.pdp.reviews.qna" />
            </div>
        </ProductProvider>
    );
}

/**
 * Product detail shell that composes the page layout with granular Suspense boundaries.
 * Regions render independently (they manage their own async via Suspense/Await),
 * while the core product content suspends only where use() data is needed.
 */
function ProductDetailView({ loaderData }: { loaderData: ProductPageData }) {
    const content = (
        <div className="bg-background">
            <Suspense fallback={<ProductContentSkeleton />}>
                <Await resolve={loaderData.product}>
                    {(product) => (
                        <ProductContent
                            product={product}
                            brandCustomObject={loaderData.brandCustomObject}
                            supplyChainCustomObject={loaderData.supplyChainCustomObject}
                            eventStore={loaderData.eventStore}
                            eventOccurrences={loaderData.eventOccurrences}
                            page={loaderData.page}
                            faqCategoryPromise={loaderData.faqCategoryPromise}
                        />
                    )}
                </Await>
            </Suspense>
            <Suspense fallback={null}>
                <Await resolve={loaderData.seoLinkingData}>
                    {({ category, parentCategoryForSeo }) => (
                        <div className="pt-8 bg-neutral-white">
                            <SeoLinkingWrapper current={category} parentForSiblings={parentCategoryForSeo} />
                        </div>
                    )}
                </Await>
            </Suspense>
        </div>
    );

    return content;
}

/**
 * Component that renders JSON-LD schema when productSchema promise resolves.
 * Must be inside Suspense boundary to ensure it streams correctly in SSR.
 */
function BreadcrumbJsonLd({ data }: { data: ProductPageData }) {
    const trail = use(data.breadcrumbItems);
    const schema = generateBreadcrumbSchema(trail.items, data.pageUrl, data.schemaSite);
    return (
        <JsonLd
            data={{
                '@context': 'https://schema.org',
                '@type': 'WebPage',
                '@id': `${data.pageUrl}#webpage`,
                url: data.pageUrl,
                mainEntity: { '@id': `${data.pageUrl}#product` },
                ...(data.schemaSite && {
                    inLanguage: data.schemaSite.language,
                    isPartOf: { '@id': `${data.schemaSite.homeUrl}#website` },
                }),
                ...(schema && { breadcrumb: schema }),
            }}
            id="breadcrumb-schema"
        />
    );
}

function JsonLdWrapper({
    productSchemaPromise,
}: {
    productSchemaPromise: Promise<ReturnType<typeof generateProductSchema> | null>;
}) {
    const productSchema = use(productSchemaPromise);
    return productSchema ? <JsonLd data={productSchema} id="product-schema" /> : null;
}

/**
 * Product page component that displays a product with its details and category breadcrumbs.
 * The page key ensures React only remounts when navigating to a different product, not variants.
 * Uses React's use() hook internally to handle async data fetching.
 * @returns JSX element representing the product page with Suspense boundary
 */
export default function ProductPage({ loaderData }: { loaderData: ProductPageData }) {
    // Use pageKey from loaderData to force remount only when productId changes
    // This prevents showing skeleton when switching variants (pid parameter)
    const pageKey = loaderData.pageKey;

    return (
        <Fragment key={pageKey}>
            <ProductDetailView loaderData={loaderData} />

            {/* Product JSON-LD Schema for SEO - render after page content so it appears at end of body flow */}
            <Suspense fallback={null}>
                <BreadcrumbJsonLd data={loaderData} />
            </Suspense>
            <Suspense fallback={null}>
                <JsonLdWrapper productSchemaPromise={loaderData.productSchema} />
            </Suspense>
        </Fragment>
    );
}
